<script lang="ts">
	import { page } from '$app/stores';
	import type { RequiredProduct } from '$lib/types';
	import type { Collection$result } from '$houdini';
	import { resolveRoute } from '$app/paths';
	import { getTranslate } from '@tolgee/svelte';
	import { customerBreadcrumbs } from '$lib/utils/breadcrumbsConstants';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import {
		type CollectionPathEntry,
		collectTreePath,
		findInTree,
		getDepth
	} from '$lib/utils/collectionTree';

	export let collections:
		| RequiredProduct['collections']['edges'][number]['node'][]
		| undefined
		| null = undefined;
	export let collectionPath: CollectionPathEntry[] | undefined = undefined;
	export let singleCollection: Collection$result['collection'] | undefined = undefined;
	let className = '';
	export { className as class };

	const { t } = getTranslate();

	type Crumb = {
		routeId: string;
		params: Record<string, string | undefined>;
		title: string;
	};

	$: INITIAL_CRUMB = {
		routeId: '/[locale]-[country]',
		params: {
			...$page.params
		},
		title: $t('breadcrumbs.home')
	} as Crumb;

	$: collectionsByTree = (() => {
		if ($page.route.id?.startsWith('/[locale]-[country]/magazin')) {
			const magazinCrumbs: Crumb[] = [
				{
					routeId: '/[locale]-[country]/magazin',
					params: $page.params,
					title: $t('links.magazine')
				}
			];

			if ($page.route.id === '/[locale]-[country]/magazin/[...handle]') {
				const magazinPart = $page.params.handle.split('/');
				magazinCrumbs.push({
					routeId: '/[locale]-[country]/magazin/[[branch]]',
					params: {
						...$page.params,
						branch: magazinPart?.[0] || 'sprenger'
					},
					title: $t(`magazinCategory${$page.data.story?.content?.articleTitle?.[0]?.category}`)
				});

				if (magazinPart.length > 1) {
					magazinCrumbs.push({
						routeId: '/[locale]-[country]/magazin/[...handle]',
						params: {
							...$page.params
						},
						title: $page.data.story?.content?.articleTitle?.[0]?.title || 'Titel Fehlt!'
					});
				}
			}
			return magazinCrumbs;
		}

		if ($page.route.id?.startsWith('/[locale]-[country]/news-press')) {
			const newsPressCrumbs: Crumb[] = [
				{
					routeId: '/[locale]-[country]/news-press',
					params: $page.params,
					title: $t('links.newsPresse')
				}
			];

			if ($page.route.id === '/[locale]-[country]/news-press/[...handle]') {
				const newsPressPart = $page.params.handle.split('/');
				newsPressCrumbs.push({
					routeId: '/[locale]-[country]/news-press/[[branch]]',
					params: {
						...$page.params,
						branch: newsPressPart?.[0] || 'sprenger'
					},
					title: $t(`newsPressCategory${$page.data.story?.content?.articleTitle?.[0]?.category}`)
				});

				if (newsPressPart.length > 1) {
					newsPressCrumbs.push({
						routeId: '/[locale]-[country]/news-press/[...handle]',
						params: {
							...$page.params
						},
						title: $page.data.story?.content?.articleTitle?.[0]?.title || 'Titel Fehlt!'
					});
				}
			}
			return newsPressCrumbs;
		}

		if ($page.route.id?.includes('customer')) {
			const customerBreadcrumbCollection: Array<Crumb> = [];
			for (let customerBreadcrumb of customerBreadcrumbs) {
				if ($page.url.pathname.includes(customerBreadcrumb.routeId)) {
					// Little workaround for bad breadcrumb tolgee key naming conventions used
					// (sometimes ending with .default sometimes not -> pain)
					const isCustomerRootCrumb: boolean = customerBreadcrumb.routeId === '/customer';
					const isAddressBookCrumb: boolean = customerBreadcrumb.routeId.includes('address-book');
					customerBreadcrumbCollection.push({
						routeId: customerBreadcrumb.routeId,
						params: $page.params,
						title: $t(
							`breadcrumbs.${
								isCustomerRootCrumb ? '' : 'customer.'
							}${customerBreadcrumb.routeId.replace(isCustomerRootCrumb ? '/' : '/customer/', '')}${
								isCustomerRootCrumb || isAddressBookCrumb ? '.default' : ''
							}`
						)
					});

					if (
						$page.params['number'] !== undefined &&
						customerBreadcrumbCollection.some((customerBreadcrumb) =>
							customerBreadcrumb.routeId.includes('orders')
						) &&
						$page.route.id?.includes('[number]')
					) {
						customerBreadcrumbCollection.push({
							routeId: '/customer/orders/[number]',
							params: $page.params,
							title: $page.params.number
						});
					}
				}
			}

			return customerBreadcrumbCollection;
		}

		if (!$page.data.collectionTree) {
			return [];
		}

		if (collections) {
			const rootCollection = collections.find((c) =>
				$page.data.collectionTree.items.some((s) => s.handle === c.handle)
			);
			const rootMaxTree = findInTree($page.data.collectionTree, rootCollection!.handle)!;

			const collectionsSorted = collections
				.filter(
					(s) =>
						!s.handle.includes('sale') &&
						!s.handle.includes('selection') &&
						!s.handle.includes('neuheiten')
				)
				.map((collection) => {
					const depth = getDepth(rootMaxTree, collection.handle);
					return {
						collection,
						depth
					};
				})
				.sort((a, b) => a.depth - b.depth);
			const deepestCollection = collectionsSorted[collectionsSorted.length - 1].collection;

			return collectTreePath(rootMaxTree, deepestCollection.handle)
				.filter((e) => !!e.link)
				.map((element, i, arr) => {
					const path = arr
						.slice(0, i + 1)
						.filter(Boolean)
						.map((e) => e.link)
						.join('/');

					return {
						routeId: '/[locale]-[country]/[...handle]',
						params: {
							...$page.params,
							handle: path.startsWith('/') ? path.slice(1) : path
						},
						title: element.title
					};
				});
		} else if (singleCollection) {
			return collectTreePath($page.data.collectionTree, singleCollection.handle)
				.filter((e) => !!e.link)
				.map((element, i, arr) => {
					const path = arr
						.slice(0, i + 1)
						.filter(Boolean)
						.map((e) => e.link)
						.join('/');

					return {
						routeId: '/[locale]-[country]/[...handle]',
						params: {
							...$page.params,
							handle: path.startsWith('/') ? path.slice(1) : path
						},
						title: element.title
					};
				});
		} else if (collectionPath) {
			return collectionPath.map((element, i, arr) => {
				const path = [
					...arr
						.slice(0, i + 1)
						.filter(Boolean)
						.map((e) => (e.url_key ? e.url_key : e.handle)),
					...(element.handle ? [element.handle] : [])
				].join('/');

				return {
					routeId: '/[locale]-[country]/[...handle]',
					params: {
						...$page.params,
						handle: path
					},
					title: element.name ? element.name : element.title
				};
			});
		}

		if ($page.route.id?.startsWith('/[locale]-[country]/about-us')) {
			const aboutCrumbs = [];
			if ($page.data.story?.name) {
				aboutCrumbs.push({
					routeId: $page.route.id,
					params: $page.params,
					title: $page.data.story.name
				});
			}
			return aboutCrumbs;
		}
		if ($page.route.id?.startsWith('/[locale]-[country]/champions')) {
			const aboutCrumbs = [];
			if ($page.data.story?.name) {
				aboutCrumbs.push({
					routeId: $page.route.id,
					params: $page.params,
					title: $page.data.story.name
				});
			}
			return aboutCrumbs;
		}

		return [];
	})();

	$: crumbs = [INITIAL_CRUMB, ...collectionsByTree];
	const isLast = (index: number) => crumbs.length - 1 === index;
</script>

<div class={classes('flex items-center text-h-12 gap-1', className)}>
	{#each crumbs as crumb, i}
		{#if !(crumb.routeId === '/[locale]-[country]/collections/[handle]' && !crumb.params.handle)}
			<a href={resolveRoute(crumb.routeId, crumb.params)} class="flex items-center">
				{crumb.title}
				{#if !isLast(i) && crumb}
					<div class="i-sprenger-chevron-right h-3 w-3 ml-2" />
				{/if}
			</a>
		{/if}
	{/each}
</div>
